import { html, css } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { TeComponent } from '../common/te-component';
import { booleanConverter } from '../common/converters';
import '../te-text';
import { theme } from '../common/theme';

/**
 * Radio Button Component
 */
@customElement('te-radio-button')
export class TeRadioButton extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-radio-button-wrapper {
        display: flex;
        align-items: center;
        gap: 16px;
      }

      input[type='radio'] {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        border-radius: 50%;
        width: 16px;
        height: 16px;
        border: 1px solid ${theme.colors.shade3};
        position: relative;
        margin: 0;
        display: grid;
        place-content: center;
        background: ${theme.colors.shade0};
      }

      input[type='radio']:checked {
        border: 1px solid ${theme.colors.primary.v0};
      }

      input[type='radio']::before {
        content: '';
        width: 12px;
        height: 12px;
        border-radius: 50%;
        transform: scale(0);
        background: ${theme.colors.primary.v0};
      }

      input[type='radio']:checked::before {
        transform: scale(1);
      }

      input[type='radio']:disabled {
        border: 1px solid ${theme.colors.shade3};
      }

      input[type='radio']:disabled:not(:checked) {
        background: transparent;
      }

      input[type='radio']:disabled::before {
        background: ${theme.colors.shade3};
      }
    `,
  ];

  @query('input') protected formElement!: HTMLInputElement;

  /** Radio button is disabled */
  @property({ type: Boolean, converter: booleanConverter }) disabled = false;
  /** Radio button group name */
  @property({ type: String }) name = '';
  /** Input value */
  @property({ type: String }) value = '';
  /** Input id */
  @property({ type: String }) id = '';
  /** Label text */
  @property({ type: String }) label = '';
  /** Checked default radio button */
  @property({ type: Boolean, converter: booleanConverter }) checked = false;

  updated() {
    this.formElement.checked = this.checked;
  }

  renderLabel() {
    if (!this.label) return;
    return html`
      <label for=${this.id}>
        <te-text @click=${this.handleClick}>${this.label}</te-text>
      </label>
    `;
  }

  render() {
    return html`
      <div class="te-radio-button-wrapper">
        <input
          type="radio"
          class="te-radio-button"
          id=${this.id}
          name=${this.name}
          value=${this.value}
          .checked=${this.checked}
          ?disabled=${this.disabled}
          @change=${this.handleChange}
          @click=${this.handleClick}
        />
        ${this.renderLabel()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-radio-button': TeRadioButton;
  }
}
